import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, ChartLineUp, Trophy, WhatsappLogo, TrendUp, TrendDown, Sparkle, Confetti } from "@phosphor-icons/react";
import { API, authHeaders } from "@/lib/api";
import { useLocale } from "@/i18n";
import { formatPrice } from "../booking/format";

export default function ToolsTab({ country }) {
  const { t, locale } = useLocale();
  const waPrefix = country === "py" ? "595" : "55";
  const [stats, setStats] = useState(null);
  const [cutsDay, setCutsDay] = useState("8");
  const [avgPrice, setAvgPrice] = useState(country === "py" ? "50000" : "30");
  const [daysWeek, setDaysWeek] = useState("6");
  const [expenses, setExpenses] = useState("");
  const [winner, setWinner] = useState(null);
  const [raffles, setRaffles] = useState([]);
  const [drawing, setDrawing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/barber/stats`, { headers: authHeaders() });
      setStats(res.data);
    } catch {
      toast.error(t("panel_error"));
    }
    try {
      const res = await axios.get(`${API}/barber/raffles`, { headers: authHeaders() });
      setRaffles(res.data);
    } catch {
      // histórico de sorteios opcional
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draw = async () => {
    setDrawing(true);
    try {
      const res = await axios.post(`${API}/barber/raffle`, {}, { headers: authHeaders() });
      setWinner(res.data);
      const list = await axios.get(`${API}/barber/raffles`, { headers: authHeaders() });
      setRaffles(list.data);
    } catch (err) {
      if (err.response?.status === 422) toast.warning(t("raffle_empty"));
      else toast.error(t("raffle_error"));
    } finally {
      setDrawing(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const revenue = (Number(cutsDay) || 0) * (Number(avgPrice.replace?.(",", ".") || avgPrice) || 0) * (Number(daysWeek) || 0) * 4.33;
  const profit = revenue - (Number(String(expenses).replace(",", ".")) || 0);

  const inputCls =
    "w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors";

  return (
    <div className="space-y-4" data-testid="tools-tab">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="month-summary">
        <h2 className="text-sm font-bold text-zinc-300 flex items-center gap-2 mb-3">
          <ChartLineUp size={16} className="text-amber-500" weight="duotone" /> {t("month_title")}
        </h2>
        {!stats ? (
          <div className="flex justify-center py-4">
            <div className="h-6 w-6 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-xl font-black text-zinc-50" data-testid="month-bookings">{stats.month_bookings}</p>
              <p className="text-[11px] text-zinc-500">{t("month_bookings")}</p>
            </div>
            <div>
              <p className="text-xl font-black text-amber-500" data-testid="month-revenue">{formatPrice(stats.month_revenue, locale)}</p>
              <p className="text-[11px] text-zinc-500">{t("month_revenue")}</p>
            </div>
            <div>
              <p className="text-xl font-black text-zinc-50" data-testid="total-clients">{stats.total_clients}</p>
              <p className="text-[11px] text-zinc-500">{t("total_clients")}</p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="calc-section">
        <h2 className="text-sm font-bold text-zinc-300 flex items-center gap-2">
          <Calculator size={16} className="text-amber-500" weight="duotone" /> {t("calc_title")}
        </h2>
        <p className="text-xs text-zinc-600 mt-1 mb-3">{t("calc_hint")}</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-zinc-400 block min-h-[2rem]">{t("calc_cuts_day")}</label>
            <input data-testid="calc-cuts-input" type="number" min="0" value={cutsDay} onChange={(e) => setCutsDay(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-400 block min-h-[2rem]">{t("calc_avg_price")}</label>
            <input data-testid="calc-price-input" type="text" inputMode="decimal" value={avgPrice} onChange={(e) => setAvgPrice(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-400 block min-h-[2rem]">{t("calc_days_week")}</label>
            <input data-testid="calc-days-input" type="number" min="0" max="7" value={daysWeek} onChange={(e) => setDaysWeek(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-400 block min-h-[2rem]">{t("calc_expenses")}</label>
            <input data-testid="calc-expenses-input" type="text" inputMode="decimal" value={expenses} onChange={(e) => setExpenses(e.target.value)} placeholder="0" className={inputCls} />
          </div>
        </div>
        <div className="mt-4 bg-zinc-950 border border-zinc-800 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-400">{t("calc_revenue")}</span>
            <span className="font-bold text-zinc-100" data-testid="calc-revenue">{formatPrice(Math.max(0, revenue), locale)}</span>
          </div>
          <div className="flex items-center justify-between text-sm border-t border-zinc-800 pt-2">
            <span className="text-zinc-400">{t("calc_profit")}</span>
            <span
              className={`font-bold flex items-center gap-1 ${profit >= 0 ? "text-emerald-400" : "text-red-400"}`}
              data-testid="calc-profit"
            >
              {profit >= 0 ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}
              {formatPrice(profit, locale)}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="top-clients-section">
        <h2 className="text-sm font-bold text-zinc-300 flex items-center gap-2 mb-3">
          <Trophy size={16} className="text-amber-500" weight="duotone" /> {t("top_title")}
        </h2>
        {!stats ? null : stats.top_clients.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="top-empty">{t("top_empty")}</p>
        ) : (
          <div className="space-y-2" data-testid="top-clients-list">
            {stats.top_clients.map((c, i) => (
              <div key={c.phone} className="flex items-center justify-between gap-3 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5" data-testid={`top-client-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`font-display text-lg font-black ${i < 3 ? "text-amber-500" : "text-zinc-600"}`}>
                    {i + 1}º
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-zinc-100 text-sm truncate">{c.name}</p>
                    <p className="text-[11px] text-zinc-500">
                      {c.count} {c.count === 1 ? t("top_visit") : t("top_visits")} · {t("top_last")} {c.last_date.split("-").reverse().join("/")}
                    </p>
                  </div>
                </div>
                <a
                  href={`https://wa.me/${c.phone.length <= 11 ? waPrefix + c.phone : c.phone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`top-client-wa-${i}`}
                  className="text-zinc-500 hover:text-emerald-400 transition-colors shrink-0"
                >
                  <WhatsappLogo size={18} weight="duotone" />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="raffle-section">
        <h2 className="text-sm font-bold text-zinc-300 flex items-center gap-2">
          <Confetti size={16} className="text-amber-500" weight="duotone" /> {t("raffle_title")}
        </h2>
        <p className="text-xs text-zinc-600 mt-1 mb-3">{t("raffle_hint")}</p>
        <button
          data-testid="raffle-draw-btn"
          onClick={draw}
          disabled={drawing}
          className="w-full h-11 rounded-lg bg-amber-500 text-zinc-950 font-bold flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
        >
          <Sparkle size={16} weight="bold" />
          {drawing ? t("raffle_drawing") : t("raffle_btn")}
        </button>
        {winner && (
          <div className="mt-3 bg-amber-500/10 border border-amber-500/40 rounded-lg p-4 text-center" data-testid="raffle-winner-card">
            <Trophy size={28} weight="duotone" className="text-amber-500 mx-auto" />
            <p className="text-[11px] uppercase tracking-widest text-amber-500/80 mt-1">{t("raffle_winner")}</p>
            <p className="text-lg font-black text-zinc-50 mt-0.5" data-testid="raffle-winner-name">{winner.client_name}</p>
            <p className="text-xs text-zinc-500">{winner.entries} {t("raffle_chances")}</p>
            <a
              href={`https://wa.me/${winner.client_phone.length <= 11 ? waPrefix + winner.client_phone : winner.client_phone}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="raffle-winner-wa"
              className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-500 text-zinc-950 text-sm font-bold hover:bg-emerald-400 transition-colors"
            >
              <WhatsappLogo size={16} weight="bold" /> {t("raffle_wa")}
            </a>
          </div>
        )}
        {raffles.length > 0 && (
          <div className="mt-4" data-testid="raffle-history">
            <p className="text-xs font-semibold text-zinc-500 mb-2">{t("raffle_history")}</p>
            <div className="space-y-1.5">
              {raffles.map((r) => (
                <div key={r.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2" data-testid={`raffle-row-${r.id}`}>
                  <span className="text-sm text-zinc-200 font-semibold truncate">{r.client_name}</span>
                  <span className="text-[11px] text-zinc-500 shrink-0">{r.month.split("-").reverse().join("/")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
